import { RequireEntityGuard } from "@core/presentacion/component/routing/require-entity/require-entity.guard";
import { RequireRoleGuard } from "@core/presentacion/component/routing/require-role/require-role.guard";
import { UserRoleEnum } from "@user/domain/models/user-role.model";
import { lazy } from "react";
import { Route } from "react-router-dom";

const EditBudgetPage = lazy(async () =>
    import(
        /* webpackChunkName: "EditBudgetPage" */ "@project/presentation/pages/edit-budget/edit-budget.page"
    ).then((module) => ({ default: module.EditBudgetPage })),
);

const ProjectListPage = lazy(async () =>
    import(
        /* webpackChunkName: "ProjectListPage" */ "@project/presentation/pages/project/list/project-list.page"
    ).then((module) => ({ default: module.ProjectListPage })),
);

const CreateProjectPage = lazy(async () =>
    import(
        /* webpackChunkName: "CreateProjectPage" */ "@project/presentation/pages/project/create/create-project.page"
    ).then((module) => ({ default: module.CreateProjectPage })),
);

const EditProjectPage = lazy(async () =>
    import(
        /* webpackChunkName: "EditProjectPage" */ "@project/presentation/pages/project/edit/edit-project.page"
    ).then((module) => ({ default: module.EditProjectPage })),
);

const DetailProjectPage = lazy(async () =>
    import(
        /* webpackChunkName: "DetailProjectPage" */ "@project/presentation/pages/project/detail/detail-project.page"
    ).then((module) => ({ default: module.ProjectDetailPage })),
);

const ListFinancialEntityPage = lazy(async () =>
    import(
        /* webpackChunkName: "ListFinancialEntityPage" */ "@project/presentation/pages/financial-entity/list/financial-entity-list.page"
    ).then((module) => ({ default: module.FinancialEntityListPage })),
);

const CreateFinancialEntityPage = lazy(async () =>
    import(
        /* webpackChunkName: "CreateFinancialEntityPage" */ "@project/presentation/pages/financial-entity/create/create-financial-entity.page"
    ).then((module) => ({ default: module.CreateFinancialEntityPage })),
);

const EditFinancialEntityPage = lazy(async () =>
    import(
        /* webpackChunkName: "EditFinancialEntityPage" */ "@project/presentation/pages/financial-entity/edit/edit-financial-entity.page"
    ).then((module) => ({ default: module.EditFinancialEntityPage })),
);

const DetailFinancialEntityPage = lazy(async () =>
    import(
        /* webpackChunkName: "DetailFinancialEntityPage" */ "@project/presentation/pages/financial-entity/detail/detail-financial-entity.page"
    ).then((module) => ({ default: module.DetailFinancialEntityPage })),
);

// DEPRECATED: Replaced by unified budget template edit flow
// const BudgetTemplateEditPage = lazy(() =>
//     import(
//         /* webpackChunkName: "BudgetTemplateEditPage" */ "@project/presentation/pages/budget-template/edit/budget-template-edit.page"
//     ).then((module) => ({ default: module.BudgetTemplateEditPage })),
// );

const CreateBudgetPage = lazy(async () =>
    import(
        /* webpackChunkName: "CreateBudgetPage" */ "@project/presentation/pages/budget/create/create-budget-template.page"
    ).then((module) => ({ default: module.CreateBudgetTemplatePage })),
);

const EditBudgetTemplatePage = lazy(async () =>
    import(
        /* webpackChunkName: "EditBudgetTemplatePage" */ "@project/presentation/pages/budget/edit/edit-budget-template.page"
    ).then((module) => ({ default: module.EditBudgetTemplatePage })),
);

export class ProjectPaths {
    static readonly Root = "projects";

    static readonly List = "list";

    static readonly Create = "create";

    static readonly EditParam = ":projectId";
    static readonly Edit = `edit/${ProjectPaths.EditParam}`;

    static readonly FinancialEntityRoot = "financial-entity";
    static readonly ListFinancialEntities = "list";
    static readonly CreateFinancialEntity = "create";

    static readonly EditFinancialEntityParam = ":financialEntityId";
    static readonly EditFinancialEntity = `edit/${ProjectPaths.EditFinancialEntityParam}`;

    static readonly FinancialEntityDetailParam = ":financialEntityId";
    static readonly FinancialEntityDetail = `detail/${ProjectPaths.FinancialEntityDetailParam}`;

    static readonly EditBudgetParam = ":projectFinancialEntityId";
    static readonly EditBudget = `${ProjectPaths.FinancialEntityDetailParam}/budget/edit/${ProjectPaths.EditBudgetParam}`;

    static readonly BudgetRoot = "budget";
    static readonly BudgetCreate = "create";
    static readonly BudgetEditParam = ":budgetId";
    static readonly BudgetEdit = `edit/${ProjectPaths.BudgetEditParam}`;

    static readonly DetailParam = ":projectId";
    static readonly Detail = `detail/${ProjectPaths.DetailParam}`;
}

export class ProjectRoutes {
    static LIST = `/${ProjectPaths.Root}/${ProjectPaths.List}`;

    static LIST_FINANCIAL_ENTITY = `/${ProjectPaths.Root}/${ProjectPaths.FinancialEntityRoot}/${ProjectPaths.ListFinancialEntities}`;

    static CREATE_FINANCIAL_ENTITY = `/${ProjectPaths.Root}/${ProjectPaths.FinancialEntityRoot}/${ProjectPaths.CreateFinancialEntity}`;

    static FINANCIAL_ENTITY_DETAIL = (financialEntityId: number): string =>
        `/${ProjectPaths.Root}/${
            ProjectPaths.FinancialEntityRoot
        }/${ProjectPaths.FinancialEntityDetail.replace(
            ProjectPaths.FinancialEntityDetailParam,
            financialEntityId.toString(),
        )}`;

    static CREATE = `/${ProjectPaths.Root}/${ProjectPaths.Create}`;

    static EDIT = (projectId: number): string =>
        `/${ProjectPaths.Root}/${ProjectPaths.Edit.replace(
            ProjectPaths.EditParam,
            projectId.toString(),
        )}`;

    static EDIT_BUDGET = (
        projectId: number,
        projectFinancialEntityId: number,
    ): string =>
        `${ProjectRoutes.EDIT(projectId)}/${ProjectPaths.EditBudget.replace(
            ProjectPaths.EditBudgetParam,
            projectFinancialEntityId.toString(),
        )}`;

    static DETAIL = (projectId: number): string =>
        `/${ProjectPaths.Root}/${ProjectPaths.Detail.replace(
            ProjectPaths.DetailParam,
            projectId.toString(),
        )}`;

    static BUDGET_CREATE = `/${ProjectPaths.Root}/${ProjectPaths.BudgetRoot}/${ProjectPaths.BudgetCreate}`;
    static BUDGET_EDIT = (budgetId: number | string): string =>
        `/${ProjectPaths.Root}/${ProjectPaths.BudgetRoot}/${ProjectPaths.BudgetEdit.replace(
            ProjectPaths.BudgetEditParam,
            budgetId.toString(),
        )}`;
}

export const projectRoutes = (
    <Route path={ProjectPaths.Root}>
        <Route
            element={<ProjectListPage />}
            path={ProjectPaths.List}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[
                        UserRoleEnum.SuperAdmin,
                        UserRoleEnum.Admin,
                        UserRoleEnum.EntityAdmin,
                    ]}
                >
                    <RequireEntityGuard redirectTo={ProjectRoutes.LIST}>
                        <CreateProjectPage />
                    </RequireEntityGuard>
                </RequireRoleGuard>
            }
            path={ProjectPaths.Create}
        />
        <Route
            element={
                <RequireRoleGuard
                    rolesIn={[
                        UserRoleEnum.SuperAdmin,
                        UserRoleEnum.Admin,
                        UserRoleEnum.EntityAdmin,
                    ]}
                >
                    <DetailProjectPage />
                </RequireRoleGuard>
            }
            path={ProjectPaths.Detail}
        />
        <Route path={ProjectPaths.Edit}>
            <Route
                index
                element={
                    <RequireRoleGuard
                        rolesIn={[
                            UserRoleEnum.SuperAdmin,
                            UserRoleEnum.Admin,
                            UserRoleEnum.EntityAdmin,
                        ]}
                    >
                        <RequireEntityGuard redirectTo={ProjectRoutes.LIST}>
                            <EditProjectPage />
                        </RequireEntityGuard>
                    </RequireRoleGuard>
                }
            />
            <Route
                path={ProjectPaths.EditBudget}
                element={
                    <RequireRoleGuard
                        rolesIn={[
                            UserRoleEnum.SuperAdmin,
                            UserRoleEnum.Admin,
                            UserRoleEnum.EntityAdmin,
                        ]}
                    >
                        <EditBudgetPage />
                    </RequireRoleGuard>
                }
            />
        </Route>
        <Route path={ProjectPaths.FinancialEntityRoot}>
            <Route
                element={
                    <RequireRoleGuard
                        rolesIn={[
                            UserRoleEnum.SuperAdmin,
                            UserRoleEnum.Admin,
                            UserRoleEnum.EntityAdmin,
                        ]}
                    >
                        <ListFinancialEntityPage />
                    </RequireRoleGuard>
                }
                path={ProjectPaths.ListFinancialEntities}
            />
            <Route
                element={
                    <RequireRoleGuard
                        rolesIn={[
                            UserRoleEnum.SuperAdmin,
                            UserRoleEnum.Admin,
                            UserRoleEnum.EntityAdmin,
                        ]}
                    >
                        <RequireEntityGuard
                            redirectTo={ProjectRoutes.LIST_FINANCIAL_ENTITY}
                        >
                            <CreateFinancialEntityPage />
                        </RequireEntityGuard>
                    </RequireRoleGuard>
                }
                path={ProjectPaths.CreateFinancialEntity}
            />
            <Route
                element={
                    <RequireRoleGuard
                        rolesIn={[
                            UserRoleEnum.SuperAdmin,
                            UserRoleEnum.Admin,
                            UserRoleEnum.EntityAdmin,
                        ]}
                    >
                        <RequireEntityGuard
                            redirectTo={ProjectRoutes.LIST_FINANCIAL_ENTITY}
                        >
                            <EditFinancialEntityPage />
                        </RequireEntityGuard>
                    </RequireRoleGuard>
                }
                path={ProjectPaths.EditFinancialEntity}
            />
            <Route
                element={
                    <RequireRoleGuard
                        rolesIn={[
                            UserRoleEnum.SuperAdmin,
                            UserRoleEnum.Admin,
                            UserRoleEnum.EntityAdmin,
                        ]}
                    >
                        <DetailFinancialEntityPage />
                    </RequireRoleGuard>
                }
                path={ProjectPaths.FinancialEntityDetail}
            />
        </Route>
        <Route path={ProjectPaths.BudgetRoot}>
            <Route
                element={
                    <RequireRoleGuard
                        rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                    >
                        <CreateBudgetPage />
                    </RequireRoleGuard>
                }
                path={ProjectPaths.BudgetCreate}
            />
            <Route
                element={
                    <RequireRoleGuard
                        rolesIn={[UserRoleEnum.SuperAdmin, UserRoleEnum.Admin]}
                    >
                        <EditBudgetTemplatePage />
                    </RequireRoleGuard>
                }
                path={ProjectPaths.BudgetEdit}
            />
        </Route>
    </Route>
);
